import type { JumpChart } from '../../jumpChart.js';
import type { JumpReplayInput, JumpReplayInputs, ReplayOutcome } from '../../replay.js';
import { computeAccuracy } from '../../scoring.js';
import { type JumpEngineEvent, type JumpEngineInput } from './core.js';
import {
  advanceJumpSim,
  cloneJumpState,
  findClearingJump,
} from './headlessRunnerPlanSegment.js';

/** Beats past a hazard the planner sims so its clearing pass is committed. */
const PLAN_COMMIT_LOOKAHEAD = 1e-3;
import {
  buildJumpEngineConfig,
  createInitialJumpState,
  resetJumpStateForNextLoop,
  type JumpEngineConfig,
  type JumpEngineState,
} from './state.js';

export interface RunJumpReplayOptions {
  stepBeats?: number;
}

export interface RunJumpReplayResult {
  outcome: ReplayOutcome;
  /** Number of full chart loops cleared (0 if the cube died in loop 1). */
  loopsCompleted: number;
}

/** Server steps the engine at 1/16 beat; the client steps per animation frame. */
const DEFAULT_STEP_BEATS = 1 / 16;

/**
 * Replay a captured Jump run through the deterministic engine. Same shape as
 * `runWavesReplay`: walk the captured `replay.loops` in order, reset the
 * chart-relative state between loops, accumulate score/combo across them.
 *
 * Determinism: the engine re-anchors the parabolic trajectory only at EVENTS
 * (input edges, analytic landings/walk-offs, jumps) — all of which fall on
 * beats identical across client and server re-sims — and samples the
 * closed-form arc at every other beat. So a press captured at a sub-ms
 * different tick boundary on client vs server still yields the same cube Y at
 * each hazard's exact beat, and therefore the same death beat / score.
 */
export function runJumpReplay(
  chart: JumpChart,
  replay: JumpReplayInputs,
  opts: RunJumpReplayOptions = {},
): RunJumpReplayResult {
  const config = buildJumpEngineConfig(chart);
  const state = createInitialJumpState(chart);
  const stepBeats = opts.stepBeats ?? DEFAULT_STEP_BEATS;

  let loopsCompleted = 0;
  for (let loopIdx = 0; loopIdx < replay.loops.length; loopIdx += 1) {
    if (loopIdx > 0) {
      resetJumpStateForNextLoop(state, chart);
    }
    const inputs: JumpEngineInput[] = replay.loops[loopIdx].inputs;
    advanceJumpSim(config, state, 0, chart.totalBeats, inputs, stepBeats);

    if (state.alive && !state.completed && state.beat < chart.totalBeats) {
      // Push the playhead to totalBeats with no further inputs (matches the
      // Waves runner's safety net for replays whose last input precedes the end).
      advanceJumpSim(config, state, state.beat, chart.totalBeats, [], stepBeats);
    }

    if (state.completed) {
      loopsCompleted += 1;
    }
    if (!state.alive) {
      break;
    }
  }

  const outcome: ReplayOutcome = {
    completed: loopsCompleted > 0,
    score: state.score,
    combo: state.combo,
    maxCombo: state.maxCombo,
    perfects: state.perfects,
    goods: state.goods,
    misses: state.misses,
    accuracy: computeAccuracy(state.perfects, state.goods, state.oks, state.misses),
    deathBeat: state.deathBeat,
    deathReason: state.deathReason ?? undefined,
  };

  return { outcome, loopsCompleted };
}

interface GreedyPlanResult {
  inputs: JumpReplayInput[];
  /** First hazard the planner could not clear, or null if every hazard cleared. */
  failedHazardId: string | null;
}

/**
 * Greedy single-loop planner. Walks hazards in beat order; for each as-yet
 * uncleared hazard it first checks whether the cube clears it with NO new input
 * (a high saw ducked under while grounded, or a low spike passed over from a
 * raised platform), and only otherwise finds a jump that clears it (engine-
 * verified). Each step commits the real state through the hazard's beat — so the
 * next decision is planned from the cube's true post-pass position (including
 * any platform it landed on, or the pit it must leap). Shared by the perfect-
 * replay builder (strict) and the chart filter (drops the first failure, retries).
 */
function greedyPlanJump(chart: JumpChart, stepBeats: number): GreedyPlanResult {
  const config: JumpEngineConfig = buildJumpEngineConfig(chart);
  const state: JumpEngineState = createInitialJumpState(chart);
  const inputs: JumpReplayInput[] = [];

  for (const hazard of config.hazards) {
    if (state.passedHazardIds.has(hazard.id)) {
      continue;
    }
    if (!state.alive) {
      return { inputs, failedHazardId: hazard.id };
    }
    // Does the cube already clear this hazard untouched? (duck-under saw, or a
    // spike passed over from a platform.) Probe on a clone; commit if it clears.
    const probe = cloneJumpState(state);
    advanceJumpSim(config, probe, probe.beat, hazard.beat + PLAN_COMMIT_LOOKAHEAD, [], stepBeats);
    if (probe.alive && probe.passedHazardIds.has(hazard.id)) {
      advanceJumpSim(config, state, state.beat, hazard.beat + PLAN_COMMIT_LOOKAHEAD, [], stepBeats);
      continue;
    }
    const jump = findClearingJump(config, state, hazard, stepBeats);
    if (jump === null) {
      return { inputs, failedHazardId: hazard.id };
    }
    inputs.push({ beat: jump.press, type: 'down' }, { beat: jump.release, type: 'up' });
    // Commit through both the hazard AND the release (a long jump releases at
    // its apex, ≈ the hazard beat, and a touch later for late-window presses)
    // so the next hazard is planned from a state whose button is already up.
    advanceJumpSim(
      config,
      state,
      state.beat,
      Math.max(hazard.beat, jump.release) + 1e-3,
      [
        { beat: jump.press, type: 'down' },
        { beat: jump.release, type: 'up' },
      ],
      stepBeats,
    );
    if (!state.alive || !state.passedHazardIds.has(hazard.id)) {
      return { inputs, failedHazardId: hazard.id };
    }
  }

  inputs.sort((a, b) => a.beat - b.beat);
  return { inputs, failedHazardId: null };
}

/**
 * Build a perfect-clear replay for a Jump chart — a press/release for every
 * obstacle, each timed (where possible) to apex over the obstacle for maximum
 * clearance. Throws if any obstacle is unclearable (the chart should have been
 * run through {@link firstUnclearableJumpHazardId} / the filter first).
 *
 * Tests use this both to assert a chart was built playably and to feed a clean
 * replay through the headless engine for an end-to-end determinism check.
 */
export function buildPerfectJumpReplayInputs(
  chart: JumpChart,
  stepBeats: number = DEFAULT_STEP_BEATS,
): JumpReplayInput[] {
  const result = greedyPlanJump(chart, stepBeats);
  if (result.failedHazardId !== null) {
    throw new Error(
      `Jump chart "${chart.slug}" is unclearable at obstacle ${result.failedHazardId}`,
    );
  }
  return result.inputs;
}

/**
 * Return the id of the first obstacle the greedy planner cannot clear, or null
 * if the whole chart is clearable. The chart-builder filter calls this, drops
 * the returned obstacle, and retries — keeping "filter accepts" ⇔ "planner
 * clears" in lockstep because both run this exact planner.
 */
export function firstUnclearableJumpHazardId(
  chart: JumpChart,
  stepBeats: number = DEFAULT_STEP_BEATS,
): string | null {
  return greedyPlanJump(chart, stepBeats).failedHazardId;
}

/** Re-exported for the determinism test (sim a hand-authored replay at a given resolution). */
export { advanceJumpSim, cloneJumpState };
export type { JumpEngineEvent };
